import { CalendarRange, Trash2 } from "lucide-react";
import type { RollupReportSummary } from "../../../types";
import { Card, CardContent } from "@/components/ui/card";
import { StatusBadge } from "@/components/ui/status-badge";
import { Button } from "@/components/ui/button";
import { formatModelLabel } from "@/lib/format";

const PERIOD_LABEL: Record<string, string> = { month: "Monthly", quarter: "Quarterly", year: "Yearly" };

interface RollupCardProps {
  rollup: RollupReportSummary;
  onClick: () => void;
  onDelete: () => void;
}

export function RollupCard({ rollup, onClick, onDelete }: RollupCardProps) {
  return (
    <Card className="cursor-pointer hover:border-brand-200 transition-colors" onClick={onClick}>
      <CardContent className="py-5 px-6">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-md bg-brand-50 border border-brand-100 flex items-center justify-center">
              <CalendarRange className="w-4 h-4 text-brand-600" />
            </div>
            <div>
              <h3 className="text-[14px] font-semibold text-brand-950">{rollup.name}</h3>
              <p className="text-[11px] text-slate-400 mt-0.5">
                {PERIOD_LABEL[rollup.period_type] || rollup.period_type}
                {rollup.model ? ` · ${formatModelLabel(rollup.model)}` : " · All models"}
              </p>
            </div>
          </div>
          <StatusBadge status={rollup.status} label={rollup.status === "generating" ? "Generating" : undefined} />
        </div>

        {rollup.headline ? (
          <p className="text-[13px] text-brand-950 font-medium leading-snug line-clamp-2">{rollup.headline}</p>
        ) : (
          <p className="text-[12px] text-slate-400">
            {rollup.status === "generating" ? "Writing recap…" : "Period recap"}
          </p>
        )}

        <div className="flex items-center justify-between mt-3">
          <p className="text-[12px] text-slate-400">
            {new Date(rollup.created_at.replace(" ", "T") + "Z").toLocaleDateString("en-US", { month: "short", day: "numeric" })}
          </p>
          <Button
            variant="ghost"
            size="icon"
            className="text-slate-400 hover:text-red-600 -mr-2"
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
